import React, { useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { PortalProvider } from './contexts/PortalContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import Companies from './pages/Companies';
import CompanyAssets from './pages/CompanyAssets';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminCompanies from './pages/admin/AdminCompanies';
import AdminAssets from './pages/admin/AdminAssets';
import AdminUsers from './pages/admin/AdminUsers';
import AdminAudit from './pages/admin/AdminAudit';
import AdminHealth from './pages/admin/AdminHealth';
import AdminSettings from './pages/admin/AdminSettings';
import './App.css';

function App() {
  useEffect(() => {
    // Apply saved workspace settings on app load and whenever settings change
    const applyWorkspaceSettings = () => {
      const backgroundImage = localStorage.getItem('workspace_background');
      const backgroundEnabled = localStorage.getItem('workspace_background_enabled') !== 'false';
      const portalTitle = localStorage.getItem('portal_title') || 'Information Portal';
      const primaryColor = localStorage.getItem('primary_color') || '#3b82f6';

      console.log('Applying workspace settings:', {
        backgroundImage,
        backgroundEnabled,
        portalTitle,
        primaryColor
      });

      // Update document title
      document.title = portalTitle;

      // Apply background to body
      const body = document.body;
      
      // Clear any existing background first
      body.style.backgroundImage = '';
      body.style.backgroundSize = '';
      body.style.backgroundPosition = '';
      body.style.backgroundRepeat = '';
      body.style.backgroundAttachment = '';
      
      // Remove existing overlay
      const existingOverlay = document.getElementById('workspace-overlay');
      if (existingOverlay) {
        existingOverlay.remove();
      }

      if (backgroundEnabled && backgroundImage && backgroundImage.trim() !== '') {
        console.log('Applying background image:', backgroundImage);
        
        body.style.backgroundImage = `url("${backgroundImage}")`;
        body.style.backgroundSize = 'cover';
        body.style.backgroundPosition = 'center';
        body.style.backgroundRepeat = 'no-repeat';
        body.style.backgroundAttachment = 'fixed';

        // Add overlay for 75% opacity effect
        const overlay = document.createElement('div');
        overlay.id = 'workspace-overlay';
        overlay.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(255, 255, 255, 0.75);
          pointer-events: none;
          z-index: -1;
        `;
        document.body.appendChild(overlay);
        
        console.log('Background overlay applied');
      } else {
        console.log('No background image to apply or background disabled');
      }

      // Apply primary color
      applyPrimaryColor(primaryColor);
    };

    // Apply primary color to CSS custom properties
    const applyPrimaryColor = (color) => {
      console.log('Applying primary color:', color);
      
      const root = document.documentElement;
      
      // Convert hex to RGB for opacity variants
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : null;
      };

      const rgb = hexToRgb(color);
      if (rgb) {
        // Set CSS custom properties for primary color variants
        root.style.setProperty('--color-primary-50', `${Math.min(255, rgb.r + 40)} ${Math.min(255, rgb.g + 40)} ${Math.min(255, rgb.b + 40)}`);
        root.style.setProperty('--color-primary-100', `${Math.min(255, rgb.r + 30)} ${Math.min(255, rgb.g + 30)} ${Math.min(255, rgb.b + 30)}`);
        root.style.setProperty('--color-primary-200', `${Math.min(255, rgb.r + 20)} ${Math.min(255, rgb.g + 20)} ${Math.min(255, rgb.b + 20)}`);
        root.style.setProperty('--color-primary-300', `${Math.min(255, rgb.r + 10)} ${Math.min(255, rgb.g + 10)} ${Math.min(255, rgb.b + 10)}`);
        root.style.setProperty('--color-primary-400', `${Math.min(255, rgb.r + 5)} ${Math.min(255, rgb.g + 5)} ${Math.min(255, rgb.b + 5)}`);
        root.style.setProperty('--color-primary-500', `${rgb.r} ${rgb.g} ${rgb.b}`);
        root.style.setProperty('--color-primary-600', `${Math.max(0, rgb.r - 10)} ${Math.max(0, rgb.g - 10)} ${Math.max(0, rgb.b - 10)}`);
        root.style.setProperty('--color-primary-700', `${Math.max(0, rgb.r - 20)} ${Math.max(0, rgb.g - 20)} ${Math.max(0, rgb.b - 20)}`);
        root.style.setProperty('--color-primary-800', `${Math.max(0, rgb.r - 30)} ${Math.max(0, rgb.g - 30)} ${Math.max(0, rgb.b - 30)}`);
        root.style.setProperty('--color-primary-900', `${Math.max(0, rgb.r - 40)} ${Math.max(0, rgb.g - 40)} ${Math.max(0, rgb.b - 40)}`);
        
        console.log('Primary color CSS variables updated');
      }
    };

    // Listen for theme changes from settings
    const handleThemeChange = (event) => {
      console.log('Theme change event received:', event.detail);
      
      if (event.detail.primaryColor) {
        applyPrimaryColor(event.detail.primaryColor);
      }
      if (event.detail.portalTitle) {
        document.title = event.detail.portalTitle;
      }
      if (event.detail.background !== undefined) {
        // Re-apply all settings when background changes
        setTimeout(applyWorkspaceSettings, 100);
      }
    };

    // Listen for localStorage changes (from other tabs/windows)
    const handleStorageChange = (event) => {
      if (event.key && (
        event.key === 'workspace_background' ||
        event.key === 'workspace_background_enabled' ||
        event.key === 'primary_color' ||
        event.key === 'portal_title'
      )) {
        console.log('Storage change detected:', event.key, event.newValue);
        setTimeout(applyWorkspaceSettings, 100);
      }
    };

    // Apply settings on initial load
    applyWorkspaceSettings();

    // Set up event listeners
    window.addEventListener('themeChanged', handleThemeChange);
    window.addEventListener('storage', handleStorageChange);

    // Also listen for a custom background change event
    window.addEventListener('backgroundChanged', applyWorkspaceSettings);

    return () => {
      window.removeEventListener('themeChanged', handleThemeChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('backgroundChanged', applyWorkspaceSettings);
    };
  }, []);

  return (
    <AuthProvider>
      <PortalProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Companies />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/companies/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CompanyAssets />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <AdminDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/companies"
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <AdminCompanies />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/assets"
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <AdminAssets />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <AdminUsers />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/audit"
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <AdminAudit />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/health"
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <AdminHealth />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute requireAdmin>
                  <Layout>
                    <AdminSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </PortalProvider>
    </AuthProvider>
  );
}

export default App;